import {
  ChangeEvent,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BACKSPACE_KEY,
  DELETE_KEY,
} from "../../../../core/constants/keyboard_keys";
import { SEGMENT_DIGITS, SEGMENT_TEXT_LENGTH } from "./constants";
import {
  buildTimeValue,
  clamp,
  formatTypedTimeText,
  getNearestOptionIndex,
  isCompleteText,
  normalizeToSegmentText,
  pad2,
  positionToSlot,
  segmentValuesFromText,
  slotToPosition,
  textFromSegmentValues,
  textFromValueProp,
  textToPairs,
} from "./timeValueHelper";
import { CustomTimeValue, SegmentValues, TimeSegmentKey } from "./types";

/** A single digit. Must stay anchored: it decides whether a key is typed input. */
const DIGIT_PATTERN = /^\d$/;

interface UseTimeValueParams {
  /** "HH:MM:SS" string or milliseconds. `undefined` means uncontrolled. */
  value?: string | number | null;
  onChange?: (value?: CustomTimeValue) => void;
  onBlur?: (value?: CustomTimeValue) => void;
  segments: TimeSegmentKey[];
  /** Highest allowed value per segment, in segment order. */
  maxima: number[];
  optionsPerSegment: number[][];
  inputRef: RefObject<HTMLInputElement>;
  /**
   * True when focus moved to another part of the widget - the dropdown lives in
   * a portal, so leaving the input for it must not report the field as blurred.
   */
  isInternalFocusTarget?: (node: Node | null) => boolean;
}

interface UseTimeValueResult {
  /** What the input displays, possibly still incomplete while typing. */
  text: string;
  segmentValues: SegmentValues;
  /** Returns the committed text, so callers can restore the caret right away. */
  setSegmentValue: (segmentIndex: number, segmentValue: number) => string;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputBlur: (e: ReactFocusEvent<HTMLInputElement>) => void;
  /** Digits and Backspace/Delete. Returns true when the key was consumed. */
  handleTypingKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => boolean;
  /** Arrow up/down on the segment under the text caret. */
  stepCaretSegment: (delta: number) => void;
  /** Left/right arrow keys, selects the neighbouring segment. */
  moveCaretSegment: (delta: number) => void;
  /** Space on the segment under the text caret: back to zeros. */
  clearCaretSegment: () => void;
}

/**
 * Owns the entered time: the masked input text, the value it maps to, and every
 * way it can change (typing, blur, arrow keys, picking an option).
 *
 * The field always holds a value: there is no empty state, and anything that
 * would clear it lands on zeros instead. `onChange` fires once the text is
 * complete; partial input is completed on blur ("09:3" becomes "09:03:00").
 */
export const useTimeValue = ({
  value,
  onChange,
  onBlur,
  segments,
  maxima,
  optionsPerSegment,
  inputRef,
  isInternalFocusTarget,
}: UseTimeValueParams): UseTimeValueResult => {
  /** Last value emitted by the picker, used to ignore the echo of a controlled parent. */
  const lastEmittedTextRef = useRef<string>("");
  const isControlledRef = useRef(value !== undefined);

  /** What an empty value falls back to, i.e. "00:00:00" for the full picker. */
  const zeroText = useMemo(
    () => textFromSegmentValues({}, segments, maxima),
    [segments, maxima],
  );

  const [text, setText] = useState(
    () => textFromValueProp(value, segments, maxima) || zeroText,
  );

  const segmentValues = useMemo(
    () => segmentValuesFromText(text, segments),
    [text, segments],
  );

  /*
   * Both are read at call time instead of being closed over: parents usually
   * pass a fresh inline `onChange`, and `segmentValues` changes with every
   * keystroke. Depending on either would rebuild the committing callbacks on
   * every render and un-memoize the dropdown columns.
   */
  const onChangeRef = useRef(onChange);
  const segmentValuesRef = useRef(segmentValues);
  useEffect(() => {
    onChangeRef.current = onChange;
    segmentValuesRef.current = segmentValues;
  });

  const emitValue = useCallback(
    (nextText: string, notify?: (value?: CustomTimeValue) => void) => {
      lastEmittedTextRef.current = nextText;
      notify?.(
        buildTimeValue(segmentValuesFromText(nextText, segments), segments),
      );
    },
    [segments],
  );

  // sync from a controlled parent, ignoring the echo of our own onChange
  useEffect(() => {
    // a form library can mount the field before its value is known (react-hook-form
    // `reset()` runs in an effect), so controlled usage is detected on first value
    if (value !== undefined) isControlledRef.current = true;
    if (!isControlledRef.current) return;

    const nextText = textFromValueProp(value, segments, maxima) || zeroText;
    if (nextText === lastEmittedTextRef.current) return;

    lastEmittedTextRef.current = nextText;
    setText(nextText);
  }, [value, segments, maxima, zeroText]);

  const commitText = useCallback(
    (nextText: string) => {
      setText(nextText);
      emitValue(nextText, onChangeRef.current);
    },
    [emitValue],
  );

  const setSegmentValue = useCallback(
    (segmentIndex: number, segmentValue: number): string => {
      const currentValues = segmentValuesRef.current;
      const nextValues: SegmentValues = {};
      segments.forEach((key, index) => {
        nextValues[key] =
          index === segmentIndex
            ? clamp(segmentValue, 0, maxima[index])
            : clamp(currentValues[key] ?? 0, 0, maxima[index]);
      });
      const nextText = textFromSegmentValues(nextValues, segments, maxima);
      commitText(nextText);
      return nextText;
    },
    [segments, commitText, maxima],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const nextText = formatTypedTimeText(input.value, maxima) || zeroText;

    /*
     * Masking can reflow the whole string ("09:30" + "4" -> "09:30:4"), which
     * leaves the browser caret in the middle of the value. Write the masked text
     * and the caret back synchronously: deferring this (to rAF or an effect) let
     * fast typing insert the next digit before the caret was corrected, which
     * scrambled the segments.
     */
    if (nextText !== input.value) {
      input.value = nextText;
      input.setSelectionRange(nextText.length, nextText.length);
    }

    setText(nextText);

    if (isCompleteText(nextText, segments.length)) {
      emitValue(nextText, onChange);
    }
  };

  const handleInputBlur = (e: ReactFocusEvent<HTMLInputElement>) => {
    const nextText = normalizeToSegmentText(text, segments, maxima) || zeroText;

    if (nextText !== text) {
      setText(nextText);
      emitValue(nextText, onChange);
    }

    // focus only moved to the dropdown, the field is still being edited
    if (isInternalFocusTarget?.(e.relatedTarget as Node | null)) return;

    emitValue(nextText, onBlur);
  };

  /** How many digit slots the text holds, i.e. 6 for "HH:MM:SS". */
  const slotCount = segments.length * SEGMENT_DIGITS;

  /**
   * The slot the caret precedes. `max` defaults to the last slot; pass
   * `slotCount` to allow the position past the last digit, which is where
   * Backspace has to start from to reach that digit at all.
   */
  const getCaretSlot = (max = slotCount - 1): number =>
    clamp(positionToSlot(inputRef.current?.selectionStart ?? 0), 0, max);

  /** Writes the text and puts the caret on a slot, both synchronously. */
  const applyText = (nextText: string, slot: number) => {
    commitText(nextText);

    const input = inputRef.current;
    if (!input) return;
    input.value = nextText;
    // one past the last slot is allowed, the browser clamps it to the text end
    const position = slotToPosition(clamp(slot, 0, slotCount));
    input.setSelectionRange(position, position);
  };

  /**
   * Rewrites the two digit group the slot belongs to, clamped to that segment's
   * maximum, and leaves the caret on `caretSlot`.
   */
  const writeSlot = (
    slot: number,
    nextPair: (current: string, isFirstDigit: boolean) => string,
    caretSlot: number,
  ) => {
    const segmentIndex = Math.floor(slot / SEGMENT_DIGITS);
    const isFirstDigit = slot % SEGMENT_DIGITS === 0;

    const pairs = textToPairs(text, segments.length);
    const written = nextPair(pairs[segmentIndex], isFirstDigit);
    pairs[segmentIndex] = pad2(clamp(Number(written), 0, maxima[segmentIndex]));

    applyText(pairs.join(":"), caretSlot);
  };

  /**
   * Types one digit into the slot under the caret, the way a native time field
   * behaves: the digit lands in the ones place, and a second digit in the same
   * segment shifts it into the tens place ("1" -> 01, then "2" -> 12).
   */
  const typeDigit = (digit: string) => {
    const slot = getCaretSlot();
    writeSlot(
      slot,
      (current, isFirstDigit) =>
        isFirstDigit ? `0${digit}` : `${current[1]}${digit}`,
      slot + 1,
    );
  };

  /** Backspace/Delete: zeroes a slot, or the whole value if all is selected. */
  const eraseDigit = (forward: boolean) => {
    const input = inputRef.current;
    const isAllSelected =
      !!text &&
      input?.selectionStart === 0 &&
      input?.selectionEnd === text.length;

    if (isAllSelected) {
      applyText(zeroText, 0);
      return;
    }

    // Backspace erases the digit before the caret, so it needs the slot the
    // caret precedes - including the one past the last digit
    const caretSlot = getCaretSlot(slotCount);
    const slot = clamp(forward ? caretSlot : caretSlot - 1, 0, slotCount - 1);
    writeSlot(
      slot,
      (current, isFirstDigit) =>
        isFirstDigit ? `0${current[1]}` : `${current[0]}0`,
      slot,
    );
  };

  /**
   * Digit and erase keys, handled here so they overwrite slots instead of being
   * inserted into the text. Returns true when the key was consumed.
   */
  const handleTypingKeyDown = (
    e: ReactKeyboardEvent<HTMLInputElement>,
  ): boolean => {
    if (e.altKey || e.ctrlKey || e.metaKey) return false;

    if (DIGIT_PATTERN.test(e.key)) {
      e.preventDefault();
      typeDigit(e.key);
      return true;
    }
    if (e.key === BACKSPACE_KEY || e.key === DELETE_KEY) {
      e.preventDefault();
      eraseDigit(e.key === DELETE_KEY);
      return true;
    }
    return false;
  };

  /** Which segment the text caret currently sits in. */
  const getCaretSegmentIndex = (): number => {
    const caret = inputRef.current?.selectionStart ?? 0;
    return clamp(
      Math.floor(caret / SEGMENT_TEXT_LENGTH),
      0,
      segments.length - 1,
    );
  };

  /** Highlights a whole segment, so the next arrow key keeps operating on it. */
  const selectSegment = (segmentIndex: number) => {
    const start = segmentIndex * SEGMENT_TEXT_LENGTH;
    inputRef.current?.setSelectionRange(start, start + SEGMENT_DIGITS);
  };

  /**
   * Resets the segment the caret sits in to zeros. Without this, typing over a
   * highlighted segment would let the browser replace it in the text and shift
   * every following segment one place to the left.
   */
  const clearCaretSegment = () => {
    const segmentIndex = getCaretSegmentIndex();

    const pairs = textToPairs(text, segments.length);
    pairs[segmentIndex] = pad2(0);
    applyText(pairs.join(":"), segmentIndex * SEGMENT_DIGITS);

    // keep it highlighted, so the next key still operates on the same segment
    selectSegment(segmentIndex);
  };

  const stepCaretSegment = (delta: number) => {
    const segmentIndex = getCaretSegmentIndex();
    const options = optionsPerSegment[segmentIndex];
    const current = segmentValues[segments[segmentIndex]];
    const isEmpty = current === undefined;
    const currentIndex = getNearestOptionIndex(options, current ?? 0);
    // an empty segment starts at the first option, whichever arrow was pressed
    const nextIndex = isEmpty
      ? 0
      : (currentIndex + delta + options.length) % options.length;

    const nextText = setSegmentValue(segmentIndex, options[nextIndex]);

    // apply the new text and re-select the segment synchronously, so holding an
    // arrow key keeps stepping the same unit instead of drifting
    const input = inputRef.current;
    if (input) {
      input.value = nextText;
      selectSegment(segmentIndex);
    }
  };

  const moveCaretSegment = (delta: number) => {
    selectSegment(
      clamp(getCaretSegmentIndex() + delta, 0, segments.length - 1),
    );
  };

  return {
    text,
    segmentValues,
    setSegmentValue,
    handleInputChange,
    handleInputBlur,
    handleTypingKeyDown,
    stepCaretSegment,
    moveCaretSegment,
    clearCaretSegment,
  };
};

export default useTimeValue;
